import React, { useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import { useHelp } from '../contexts/HelpContext';

/**
 * Floating bottom-LEFT pill that opens the help drawer. Bottom-left
 * (not right) because Sentry's feedback widget auto-injects at
 * bottom-right; keeping them on opposite corners avoids collision and
 * separates the mental model — bug-report on the right, reference
 * lookup on the left.
 *
 * Also installs a global "?" keyboard shortcut (GitHub/Linear/Notion
 * convention). The shortcut is suppressed while the user is typing in
 * a form field so it doesn't hijack legitimate "?" characters.
 */
const HelpButton: React.FC = () => {
  const { isOpen, toggle } = useHelp();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?') return;
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tag = t.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (t.isContentEditable) return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Open user guide"
      title="User guide (?)"
      // On phones, lift above the MobileTabBar which is ~56px tall
      // PLUS `env(safe-area-inset-bottom)` on iOS devices with a
      // home indicator (~34px). The previous static `bottom-20`
      // (80px) sat inside the tab bar's footprint on iPhones, so
      // the button rendered behind the higher-z tab bar — the
      // "help button is a little hidden" PWA report.
      //
      // The arbitrary calc puts the button 24px above the tab bar's
      // top edge regardless of safe-area inset. On md+ the tab bar
      // is hidden, so `md:bottom-6` returns to the original 24px
      // placement.
      style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}
      className={`fixed md:!bottom-6 left-6 z-30 w-12 h-12 rounded-full bg-amber-600 hover:bg-amber-700 text-white shadow-lg flex items-center justify-center transition-all focus-ring ${
        isOpen ? 'scale-90 opacity-60' : 'scale-100 opacity-100'
      }`}
    >
      <HelpCircle className="w-6 h-6" />
    </button>
  );
};

export default HelpButton;
